// src/components/marketing/EditScheduledPost.tsx
// Edición de posts programados desde el Calendario de publicaciones.
//
// - <EditScheduledPost/>: fecha/hora (datetime-local, hora LOCAL del
//   navegador → ISO UTC) + caption con contador. Modo `quickToday` abre con
//   selector rápido de hora para "necesito uno hoy".
// - <CancelScheduledPost/>: pasa a borrador (quita la fecha) o elimina.
//
// Ambos aceptan `siblings`: el mismo contenido programado en otra red
// (mismo caption + mismo scheduled_at, p. ej. IG reel + FB feed). Con la
// casilla marcada, el cambio se aplica a todos.
//
// Backend: PUT /admin/posts/{id} (ScheduledPostUpdateIn) y DELETE.

import React, { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, Undo2, X } from 'lucide-react';

import {
  ApiError, deletePost, updatePost, type Platform, type ScheduledPost,
} from '@/lib/api_social_publish';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string;
  message?: string;
  duration?: number;
}) => void;

// Límite de caption de Instagram (Facebook acepta más; usamos el más estricto
// cuando el grupo incluye IG).
const IG_CAPTION_LIMIT = 2200;
const FB_CAPTION_LIMIT = 63206;
const QUICK_HOURS = [9, 12, 14, 17, 19, 21];

const PLATFORM_LABEL: Record<Platform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
};

export const EDITABLE_STATUSES = ['draft', 'scheduled', 'failed'] as const;

export function isEditable(post: ScheduledPost): boolean {
  return (EDITABLE_STATUSES as readonly string[]).includes(post.status);
}

/** Posts del mismo contenido en otras redes: mismo caption + mismo
 *  scheduled_at, distinto id, también editables. */
export function findSiblings(post: ScheduledPost, all: ScheduledPost[]): ScheduledPost[] {
  if (!post.scheduled_at) return [];
  const t = new Date(post.scheduled_at).getTime();
  return all.filter(
    (p) =>
      p.id !== post.id &&
      isEditable(p) &&
      p.caption === post.caption &&
      !!p.scheduled_at &&
      new Date(p.scheduled_at).getTime() === t,
  );
}

function siblingsLabel(siblings: ScheduledPost[]): string {
  const names = Array.from(new Set(siblings.map((s) => PLATFORM_LABEL[s.platform] || s.platform)));
  return `Aplicar también a ${names.join(' / ')} (${siblings.length} post${siblings.length > 1 ? 's' : ''} con el mismo contenido)`;
}

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

// ── datetime-local helpers (hora LOCAL del navegador) ────────

function pad(n: number) { return String(n).padStart(2, '0'); }

function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function todayAt(hour: number): Date {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return d;
}

// ═══════════════════════════════════════════════════════════════
// Modal de edición
// ═══════════════════════════════════════════════════════════════

export function EditScheduledPost({
  post,
  siblings,
  quickToday,
  addNotification,
  onClose,
  onSaved,
}: {
  post: ScheduledPost;
  siblings: ScheduledPost[];
  quickToday?: boolean;
  addNotification?: Notifier;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial = useMemo(() => {
    if (quickToday) {
      const next = QUICK_HOURS.find((h) => todayAt(h).getTime() > Date.now());
      return next !== undefined ? toLocalInput(todayAt(next)) : '';
    }
    return post.scheduled_at ? toLocalInput(new Date(post.scheduled_at)) : '';
  }, [post.scheduled_at, quickToday]);

  const [when, setWhen] = useState(initial);
  const [caption, setCaption] = useState(post.caption);
  const [applyAll, setApplyAll] = useState(siblings.length > 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targets = applyAll ? [post, ...siblings] : [post];
  const limit = targets.some((p) => p.platform === 'instagram') ? IG_CAPTION_LIMIT : FB_CAPTION_LIMIT;
  const whenDate = when ? new Date(when) : null;
  const isPast = !!whenDate && whenDate.getTime() <= Date.now();
  const tooLong = caption.length > limit;
  const invalid = !whenDate || Number.isNaN(whenDate.getTime()) || isPast || tooLong;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (invalid || !whenDate) return;
    setSaving(true);
    setError(null);
    const iso = whenDate.toISOString(); // ISO con zona (UTC, sufijo Z)
    const failures: string[] = [];
    for (const p of targets) {
      try {
        // status=scheduled: un borrador o un fallido con nueva fecha vuelve a
        // la cola del beat (el backend solo auto-promueve draft→scheduled).
        await updatePost(p.id, { scheduled_at: iso, caption, status: 'scheduled' });
      } catch (err) {
        failures.push(`${PLATFORM_LABEL[p.platform] || p.platform} #${p.id}: ${errMsg(err, 'Error desconocido')}`);
      }
    }
    setSaving(false);
    if (failures.length === targets.length) {
      setError(failures.join('\n'));
      return;
    }
    if (failures.length > 0) {
      addNotification?.({
        type: 'error',
        title: 'Guardado parcial',
        message: failures.join(' · '),
      });
    } else {
      addNotification?.({
        type: 'success',
        title: targets.length > 1 ? `${targets.length} posts actualizados` : 'Post actualizado',
        message: whenDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
        duration: 4000,
      });
    }
    onSaved();
  };

  return (
    <ModalShell onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <ModalHeader
          title={quickToday ? 'Mover a hoy' : 'Editar publicación'}
          subtitle={`${PLATFORM_LABEL[post.platform] || post.platform} · ${post.post_type}`}
          onClose={onClose}
        />

        <div className="mb-3">
          <div className="text-xs text-white/60 mb-1">Hoy a las…</div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_HOURS.map((h) => {
              const d = todayAt(h);
              const past = d.getTime() <= Date.now();
              const selected = when === toLocalInput(d);
              return (
                <button
                  key={h}
                  type="button"
                  disabled={past}
                  onClick={() => setWhen(toLocalInput(d))}
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                    selected
                      ? 'border-[var(--acc1,#00e5ff)]/60 bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)]'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  } disabled:opacity-30 disabled:cursor-not-allowed`}
                >
                  {pad(h)}:00
                </button>
              );
            })}
          </div>
        </div>

        <label className="block mb-3">
          <span className="block text-xs text-white/60 mb-1">
            Fecha y hora <span className="text-white/40">(hora local de tu navegador)</span>
          </span>
          <input
            type="datetime-local"
            value={when}
            min={toLocalInput(new Date())}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none"
            required
          />
          {isPast && (
            <span className="block text-xs text-red-300 mt-1">La fecha debe ser en el futuro.</span>
          )}
          {!when && (
            <span className="block text-xs text-white/50 mt-1">Elige una fecha y hora.</span>
          )}
        </label>

        <label className="block mb-3">
          <span className="flex items-center text-xs text-white/60 mb-1">
            Texto
            <span className="flex-1" />
            <span className={tooLong ? 'text-red-300' : 'text-white/40'}>
              {caption.length.toLocaleString()} / {limit.toLocaleString()}
            </span>
          </span>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none text-sm"
          />
        </label>

        {siblings.length > 0 && (
          <label className="flex items-start gap-2 mb-3 text-xs text-white/80 cursor-pointer">
            <input
              type="checkbox"
              checked={applyAll}
              onChange={(e) => setApplyAll(e.target.checked)}
              className="mt-0.5"
            />
            <span>{siblingsLabel(siblings)}</span>
          </label>
        )}

        {error && <ErrorBox message={error} />}

        <footer className="flex items-center justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="o-btn-ghost px-3 py-1.5 rounded-md text-sm">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={invalid || saving}
            className="px-3 py-1.5 rounded-md bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Guardar
          </button>
        </footer>
      </form>
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════════
// Cancelar publicación (confirmación)
// ═══════════════════════════════════════════════════════════════

export function CancelScheduledPost({
  post,
  siblings,
  addNotification,
  onClose,
  onDone,
}: {
  post: ScheduledPost;
  siblings: ScheduledPost[];
  addNotification?: Notifier;
  onClose: () => void;
  onDone: () => void;
}) {
  const [applyAll, setApplyAll] = useState(siblings.length > 0);
  const [busy, setBusy] = useState<'draft' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const targets = applyAll ? [post, ...siblings] : [post];

  const run = async (mode: 'draft' | 'delete') => {
    setBusy(mode);
    setError(null);
    const failures: string[] = [];
    for (const p of targets) {
      try {
        if (mode === 'draft') {
          // El backend re-promueve draft→scheduled si hay fecha, así que para
          // sacarlo de la cola hay que quitar scheduled_at.
          await updatePost(p.id, { status: 'draft', scheduled_at: null });
        } else {
          await deletePost(p.id);
        }
      } catch (err) {
        failures.push(`${PLATFORM_LABEL[p.platform] || p.platform} #${p.id}: ${errMsg(err, 'Error desconocido')}`);
      }
    }
    setBusy(null);
    if (failures.length === targets.length) {
      setError(failures.join('\n'));
      return;
    }
    addNotification?.(
      failures.length
        ? { type: 'error', title: 'Cancelación parcial', message: failures.join(' · ') }
        : {
            type: 'success',
            title: mode === 'draft' ? 'Publicación cancelada (borrador)' : 'Publicación eliminada',
            duration: 4000,
          },
    );
    onDone();
  };

  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title="Cancelar publicación" subtitle={post.caption.slice(0, 80) || '(sin texto)'} onClose={onClose} />
      <p className="text-sm text-white/70 mb-3">
        ¿Qué quieres hacer con este post? <strong>Pasar a borrador</strong> lo saca de la cola y del
        calendario (conserva texto y medios). <strong>Eliminar</strong> lo borra definitivamente.
      </p>

      {siblings.length > 0 && (
        <label className="flex items-start gap-2 mb-3 text-xs text-white/80 cursor-pointer">
          <input
            type="checkbox"
            checked={applyAll}
            onChange={(e) => setApplyAll(e.target.checked)}
            className="mt-0.5"
          />
          <span>{siblingsLabel(siblings)}</span>
        </label>
      )}

      {error && <ErrorBox message={error} />}

      <footer className="flex flex-wrap items-center justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="o-btn-ghost px-3 py-1.5 rounded-md text-sm">
          Volver
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => run('draft')}
          className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 text-sm flex items-center gap-1 disabled:opacity-50"
        >
          {busy === 'draft' ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
          Pasar a borrador
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => run('delete')}
          className="px-3 py-1.5 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/25 text-sm flex items-center gap-1 disabled:opacity-50"
        >
          {busy === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          Eliminar
        </button>
      </footer>
    </ModalShell>
  );
}

// ── Piezas compartidas (mismo estilo que ComposePost) ────────

function ModalShell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[var(--panel,#1e1f2b)] p-5 max-h-[90vh] overflow-auto">
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, subtitle, onClose }: { title: string; subtitle?: string; onClose: () => void }) {
  return (
    <header className="flex items-start gap-2 mb-4">
      <div className="min-w-0">
        <h2 className="text-base font-semibold">{title}</h2>
        {subtitle && <div className="text-xs text-white/50 truncate">{subtitle}</div>}
      </div>
      <div className="flex-1" />
      <button
        type="button"
        onClick={onClose}
        className="o-btn-ghost p-1 rounded-md text-white/60 hover:text-white"
        aria-label="Cerrar"
      >
        <X size={16} />
      </button>
    </header>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2 whitespace-pre-line">
      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
      <div>{message}</div>
    </div>
  );
}
